import { memo, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { format } from 'date-fns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import {
  DateCalendar,
  DateCalendarProps,
} from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import { Badge, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';

export interface HighlightedDaysMappings {
  [dateString: string]: Record<string, string>;
}

export interface DynamicCalendarInputProps
  extends Omit<DateCalendarProps, 'slots' | 'slotProps' | 'loading'> {
  isLoading?: boolean;
  highlightedDays?: string[]; // formatted as 'yyyy-MM-dd'
  highlightedDaysMappings?: HighlightedDaysMappings;
  highlightedDaysMappingsNameKey?: string;
}

export const DynamicCalendarInput = memo(function DynamicCalendarInput({
  isLoading,
  highlightedDays = [],
  highlightedDaysMappings,
  highlightedDaysMappingsNameKey = 'description',
  ...otherProps
}: DynamicCalendarInputProps) {
  return (
    <DateCalendar
      loading={isLoading}
      renderLoading={() => <DayCalendarSkeleton />}
      slots={{
        day: HighlightedDay as any,
      }}
      slotProps={{
        day: {
          highlightedDays,
          highlightedDaysMappings,
          highlightedDaysMappingsNameKey,
        } as any,
      }}
      {...otherProps}
    />
  );
});

interface HighlightedDayProps extends PickersDayProps {
  highlightedDays?: string[];
  highlightedDaysMappings?: HighlightedDaysMappings;
  highlightedDaysMappingsNameKey?: string;
}

function HighlightedDay(props: HighlightedDayProps) {
  const {
    highlightedDays = [],
    day,
    highlightedDaysMappings,
    highlightedDaysMappingsNameKey = 'description',
    ...other
  } = props;
  const { classes } = useStyles();

  const formattedDay = format(day, 'yyyy-MM-dd');

  const isSelected = useMemo(
    () => highlightedDays.some((d) => d === formattedDay),
    [formattedDay, highlightedDays],
  );

  const dayName =
    highlightedDaysMappings?.[formattedDay]?.[highlightedDaysMappingsNameKey] ??
    '';

  return (
    <Tooltip title={dayName} placement='top'>
      <Badge
        key={day.toString()}
        overlap='circular'
        variant='dot'
        classes={{ badge: isSelected ? classes.badge : undefined }}
        badgeContent={isSelected ? ' ' : undefined}
      >
        <PickersDay {...other} day={day} />
      </Badge>
    </Tooltip>
  );
}

const useStyles = makeStyles({ name: { DynamicCalendarInput } })(
  (theme: Theme) => ({
    badge: {
      backgroundColor: '#FF4F5B',
      right: '23%',
    },
  }),
);
